import React from "react";
import moment from "moment";

const STATUS_CONFIG = {
    SETTLED: {
        icon: "✓",
        title: "Payment Settled",
        headerBg: "#388E3C",
        bodyBg: "#f9fff9",
        accentBorder: "#c8e6c9",
        accentColor: "#2e7d32",
        stepLabel: "What happens next",
        whatHappened:
            "The super contribution has been fully processed and settled. Your account has been debited and the funds have been successfully sent to each artist's nominated super fund. This is the final confirmed status.",
        steps: [
            "The super fund will now apply the contribution to each artist's member account. This is handled entirely by the fund.",
            "If an artist cannot see the contribution in their super account after 5 business days, ask them to contact their super fund directly with the Reference number.",
            "Keep the Reference number and Transaction ID for your records — they can be used to reconcile this payment in your accounting software."
        ]
    },
    SETTLED_WITH_WARNING: {
        icon: "✓",
        title: "Payment Settled with Warnings",
        headerBg: "#689F38",
        bodyBg: "#f9fff9",
        accentBorder: "#dce775",
        accentColor: "#558B2F",
        stepLabel: "What happens next",
        whatHappened:
            "The super contribution has been settled, but one or more minor non-critical issues were noted during processing. The funds have been sent to the super fund(s), however some items may require follow-up.",
        steps: [
            "Review any warning details provided by the payment gateway and check each artist's super fund account.",
            "If an artist cannot see the contribution after 5 business days, ask them to contact their super fund with the Reference number.",
            "Keep the Reference number and Transaction ID for your records."
        ]
    },
    SUBMITTED: {
        icon: "↑",
        title: "Payment Submitted Successfully",
        headerBg: "#1565C0",
        bodyBg: "#f5f9ff",
        accentBorder: "#bbdefb",
        accentColor: "#1565C0",
        stepLabel: "What happens next",
        whatHappened:
            "The payment has been submitted to the payment gateway and is currently being processed. The funds have not yet been debited from your account.",
        steps: [
            "The payment gateway will process the contribution and debit your account in the next 1–2 banking days. Note: submissions after 4pm AEDT, or on weekends and public holidays, are counted from the next banking day.",
            "You will be able to sync the payment status here to check for updates.",
            "Once settled, the super fund will apply the contribution to each artist's member account."
        ]
    },
    EMPLOYER_PAYMENT: {
        icon: "⟳",
        title: "Debit Underway",
        headerBg: "#1976D2",
        bodyBg: "#f5f9ff",
        accentBorder: "#bbdefb",
        accentColor: "#1565C0",
        stepLabel: "What happens next",
        whatHappened:
            "Payment has been initiated — the clearing house has acknowledged the contribution and the direct debit from your account is now underway.",
        steps: [
            "No action is required from you at this stage.",
            "The direct debit typically clears within 1–2 business days.",
            "Sync the payment status here to check for updates."
        ]
    },
    DEBIT_CLEARED: {
        icon: "⟳",
        title: "Debit Successful — Transferring to Fund",
        headerBg: "#0288D1",
        bodyBg: "#f5f9ff",
        accentBorder: "#b3e5fc",
        accentColor: "#0277BD",
        stepLabel: "What happens next",
        whatHappened:
            "The direct debit was successful — money has left your account. The funds are now being prepared for transfer to the nominated super fund(s).",
        steps: [
            "No action is required from you at this stage.",
            "The funds will be transmitted to each artist's super fund shortly.",
            "Sync the payment status here to check for updates."
        ]
    },
    FUNDS_IN_TRANSIT: {
        icon: "⟳",
        title: "Funds in Transit to Super Fund",
        headerBg: "#00838F",
        bodyBg: "#f5fafa",
        accentBorder: "#b2ebf2",
        accentColor: "#006064",
        stepLabel: "What happens next",
        whatHappened:
            "Money has cleared the source account and is currently being transferred to the nominated super fund(s). The funds are on their way.",
        steps: [
            "No action is required from you at this stage.",
            "The super fund will acknowledge receipt once the transfer arrives.",
            "Sync the payment status here to check for updates."
        ]
    },
    AWAITING_FUND_RESPONSE: {
        icon: "⟳",
        title: "Paid to Funds — Awaiting Acknowledgement",
        headerBg: "#0288D1",
        bodyBg: "#f5f9ff",
        accentBorder: "#b3e5fc",
        accentColor: "#0277BD",
        stepLabel: "What happens next",
        whatHappened:
            "The funds have been received by the clearing house, which is now awaiting the super fund's processing decision. Fund processing typically takes 3–7 banking days.",
        steps: [
            "No action is required from you at this stage.",
            "Sync the payment status here to check for updates.",
            "Once the fund processes the contribution (typically within 3–7 banking days), the status will update to Settled, Settled with Warning, or Refunded."
        ]
    },
    NEW: {
        icon: "○",
        title: "Payment Created — Not Yet Submitted",
        headerBg: "#757575",
        bodyBg: "#fafafa",
        accentBorder: "#e0e0e0",
        accentColor: "#616161",
        stepLabel: "What happens next",
        whatHappened:
            "A super payment record has been created but has not yet been submitted to the payment gateway.",
        steps: [
            "Return to the Super Ledgers page and click Pay Super to submit this payment.",
            "Once submitted, the payment will be processed by the gateway and the status will update."
        ]
    },
    PENDING: {
        icon: "○",
        title: "Payment Created — Not Yet Submitted",
        headerBg: "#E65100",
        bodyBg: "#fff8f5",
        accentBorder: "#ffe0b2",
        accentColor: "#BF360C",
        stepLabel: "What happens next",
        whatHappened:
            "A super payment record has been created and is pending submission to the payment gateway.",
        steps: [
            "Return to the Super Ledgers page and click Pay Super to submit this payment.",
            "Once submitted, the payment will be processed by the gateway and the status will update."
        ]
    },
    FAILED: {
        icon: "⚠",
        title: "Payment Failed",
        headerBg: "#C62828",
        bodyBg: "#fff8f8",
        accentBorder: "#ffcdd2",
        accentColor: "#B71C1C",
        stepLabel: "What to do next",
        whatHappened:
            "This payment could not be processed. The funds have not been sent to any super fund. This may be due to a failed debit, incorrect bank account details, or a fund transfer error.",
        steps: [
            "Check that your bank account linked to HG Super Pay has sufficient funds and correct details.",
            "Review any failed payee details in the list below and correct their super fund information if required.",
            "Return to Super Ledgers and click Pay Super to retry the payment.",
            "If the problem persists, contact HG Super Pay support with the Reference number."
        ]
    },
    DEBIT_FAILED: {
        icon: "⚠",
        title: "Payment Failed — Debit Error",
        headerBg: "#C62828",
        bodyBg: "#fff8f8",
        accentBorder: "#ffcdd2",
        accentColor: "#B71C1C",
        stepLabel: "What to do next",
        whatHappened:
            "The payment gateway attempted to debit your account but the debit was rejected. This is commonly caused by insufficient funds, an incorrect bank account, or a bank-side block on the transaction.",
        steps: [
            "Check that your bank account linked to HG Super Pay has sufficient funds.",
            "Verify that your bank account details are correct in your financial profile.",
            "Contact your bank if the debit continues to fail — they may have placed a block on the payment.",
            "Once resolved, return to Super Ledgers and click Pay Super to retry this payment."
        ]
    },
    FUND_TRANSFER_FAILED: {
        icon: "⚠",
        title: "Payment Failed — Fund Transfer Error",
        headerBg: "#C62828",
        bodyBg: "#fff8f8",
        accentBorder: "#ffcdd2",
        accentColor: "#B71C1C",
        stepLabel: "What to do next",
        whatHappened:
            "The payment reached the payment gateway but the transfer to one or more super funds was rejected. This is commonly caused by incorrect super fund details — for example, an invalid member number, USI, or fund that is no longer accepting contributions.",
        steps: [
            "Contact the affected artist(s) and ask them to confirm their current super fund name, member number, and USI.",
            "Update their super fund details on their artist profile under the Superannuation section.",
            "Once the details are corrected, return to Super Ledgers and click Pay Super to retry this payment.",
            "If the problem persists, ask the artist to contact their super fund directly to confirm they are accepting contributions."
        ]
    },
    REFUNDED: {
        icon: "↩",
        title: "Payment Refunded",
        headerBg: "#D84315",
        bodyBg: "#fff8f5",
        accentBorder: "#ffccbc",
        accentColor: "#BF360C",
        stepLabel: "What to do next",
        whatHappened:
            "This payment has been refunded by the payment gateway. The funds have been returned to your nominated account. The super contribution has not been applied to any artist's super fund.",
        steps: [
            "Investigate the reason for the refund — it may be due to incorrect fund details or a failed validation.",
            "Correct any super fund details for the affected artists if required.",
            "Return to Super Ledgers and click Pay Super to resubmit the payment."
        ]
    },
    CANCELLED: {
        icon: "✕",
        title: "Payment Cancelled",
        headerBg: "#B71C1C",
        bodyBg: "#fff8f8",
        accentBorder: "#ffcdd2",
        accentColor: "#B71C1C",
        stepLabel: "What to do next",
        whatHappened:
            "This payment was cancelled before it was processed. No funds have been debited from your account and no super contributions have been made.",
        steps: [
            "If the cancellation was unintentional, return to Super Ledgers and click Pay Super to submit a new payment.",
            "If you need to investigate the cancellation reason, contact HG Super Pay support."
        ]
    },
    "PARTIALLY PAID": {
        icon: "⚠",
        title: "Partially Paid",
        headerBg: "#F57F17",
        bodyBg: "#fffdf0",
        accentBorder: "#ffe082",
        accentColor: "#E65100",
        stepLabel: "What to do next",
        whatHappened:
            "Some contributions in this payment were processed successfully, but one or more could not be applied — for example, because the artist is no longer a member of their nominated super fund. The refunded amounts have been returned to your account.",
        steps: [
            "Check the payee list below to see which contributions were refunded.",
            "Correct the affected artist's super fund details and resubmit a new payment for the refunded amount.",
            "If you need help, contact HG Super Pay support with the Reference number."
        ]
    },
    MANUAL: {
        icon: "✓",
        title: "Recorded as Manually Settled",
        headerBg: "#00695C",
        bodyBg: "#f5fafa",
        accentBorder: "#b2dfdb",
        accentColor: "#00695C",
        stepLabel: "What happens next",
        whatHappened:
            "This super payment was recorded manually as settled. It was not processed through the HG Super Pay gateway — you have confirmed that the funds have been paid directly to the super fund(s) outside of this system.",
        steps: [
            "Ensure you retain a record of the direct payment (e.g. bank transfer receipt) for reconciliation purposes.",
            "The super ledger items linked to this payment have been marked as paid."
        ]
    }
};

const FALLBACK_CONFIG = {
    icon: "○",
    title: "Payment Status Unknown",
    headerBg: "#757575",
    bodyBg: "#fafafa",
    accentBorder: "#e0e0e0",
    accentColor: "#616161",
    stepLabel: "What happens next",
    whatHappened: "The current status of this payment could not be determined.",
    steps: [
        "Try syncing the payment status using the Sync button.",
        "If the problem persists, contact support."
    ]
};

function formatDate(value) {
    if (!value) return null;
    const m = moment(value);
    return m.isValid() ? m.format("DD MMM YYYY [at] h:mm A") : value;
}

function MetaItem({ label, value, mono }) {
    if (!value) return null;
    return (
        <div>
            <div style={{ fontSize: 11, color: "#999", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>
                {label}
            </div>
            <div style={{ fontSize: 13, fontWeight: mono ? 400 : 600, color: mono ? "#666" : "#333", fontFamily: mono ? "monospace" : "inherit" }}>
                {value}
            </div>
        </div>
    );
}

const PAYMENT_STATUS_MAP = {
    "POSTED":            "SUBMITTED",
    "SETTLED":           "SETTLED",
    "SETTLED_WARNING":   "SETTLED_WITH_WARNING",
    "FAILED":            "DEBIT_FAILED",
    "DEBIT_FAILED":      "DEBIT_FAILED",
    "FUND_TRANSFER_FAILED": "FUND_TRANSFER_FAILED",
    "CANCELLED":         "CANCELLED",
    "REFUNDED":          "REFUNDED",
    "PAID":              "SETTLED",
    "PAID TO FUNDS":     "AWAITING_FUND_RESPONSE",
    "PAID_TO_FUNDS":     "AWAITING_FUND_RESPONSE",
    "NEW":               "NEW",
    "PENDING":           "PENDING",
    "EMPLOYER_PAYMENT":  "EMPLOYER_PAYMENT",
    "DEBIT_CLEARED":     "DEBIT_CLEARED",
    "FUNDS_IN_TRANSIT":  "FUNDS_IN_TRANSIT",
};

export function getStatusDisplayInfo(rawStatus) {
    if (!rawStatus) {
        return { label: "Unknown", bg: FALLBACK_CONFIG.accentBorder, color: FALLBACK_CONFIG.accentColor };
    }
    const upper = rawStatus.toUpperCase();
    const key = STATUS_CONFIG[upper] ? upper : (PAYMENT_STATUS_MAP[upper] || upper);
    const config = STATUS_CONFIG[key] || FALLBACK_CONFIG;
    return { label: config.title, bg: config.accentBorder, color: config.accentColor };
}

function SuperPayStatusPanel({ mygigstersStatus, sourceType, paymentStatus, completedOn, paymentDate, totalAmount, reference, transactionId, currencySymbol }) {
    const isManual = sourceType && sourceType.toUpperCase() === "MANUAL";

    let config;
    let displayStatus;

    if (isManual && !mygigstersStatus) {
        config = STATUS_CONFIG.MANUAL;
        displayStatus = "MANUAL";
    } else if (mygigstersStatus) {
        const upper = mygigstersStatus.toUpperCase();
        const key = STATUS_CONFIG[upper] ? upper : (PAYMENT_STATUS_MAP[upper] || upper);
        config = STATUS_CONFIG[key] || FALLBACK_CONFIG;
        displayStatus = mygigstersStatus;
    } else if (paymentStatus) {
        const key = PAYMENT_STATUS_MAP[paymentStatus.toUpperCase()] || paymentStatus.toUpperCase();
        config = STATUS_CONFIG[key] || FALLBACK_CONFIG;
        displayStatus = paymentStatus;
    } else {
        return null;
    }

    const dateLabel = ["DEBIT_FAILED", "FUND_TRANSFER_FAILED", "REFUNDED", "CANCELLED"].includes(
        (mygigstersStatus || "").toUpperCase()
    ) ? "Attempted On" : (isManual ? "Recorded On" : "Completed On");

    const dateValue = formatDate(completedOn || paymentDate);
    const amountDisplay = totalAmount != null
        ? `${currencySymbol || "$"}${parseFloat(totalAmount).toFixed(2)}`
        : null;

    return (
        <div style={{ marginBottom: 24, border: `1px solid ${config.headerBg}`, borderRadius: 6, overflow: "hidden" }}>
            <div style={{ background: config.headerBg, padding: "12px 20px", display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18, color: "#fff", fontWeight: 700, lineHeight: 1 }}>{config.icon}</span>
                <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>{config.title}</span>
                {displayStatus && (
                    <span style={{
                        marginLeft: "auto",
                        background: "rgba(255,255,255,0.2)",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "2px 10px",
                        borderRadius: 20,
                        letterSpacing: "0.08em",
                        whiteSpace: "nowrap"
                    }}>
                        {displayStatus}
                    </span>
                )}
            </div>
            <div style={{ background: config.bodyBg, padding: "16px 20px" }}>
                {(dateValue || amountDisplay || reference || transactionId) && (
                    <div style={{ display: "flex", gap: 32, marginBottom: 16, flexWrap: "wrap" }}>
                        <MetaItem label={dateLabel} value={dateValue} />
                        <MetaItem label="Amount" value={amountDisplay} />
                        <MetaItem label="Reference" value={reference} />
                        <MetaItem label="Transaction ID" value={transactionId} mono />
                    </div>
                )}
                <div style={{ background: "#fff", border: `1px solid ${config.accentBorder}`, borderRadius: 4, padding: "12px 14px", marginBottom: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: config.accentColor, marginBottom: 6 }}>What happened</div>
                    <div style={{ fontSize: 13, color: "#444", lineHeight: 1.6 }}>{config.whatHappened}</div>
                </div>
                {config.steps && config.steps.length > 0 && (
                    <div style={{ background: "#fff", border: "1px solid #ddd", borderRadius: 4, padding: "12px 14px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#333", marginBottom: 8 }}>{config.stepLabel}</div>
                        <ol style={{ margin: 0, paddingLeft: 18 }}>
                            {config.steps.map((step, i) => (
                                <li key={i} style={{ fontSize: 13, color: "#444", lineHeight: 1.7, marginBottom: 2 }}>{step}</li>
                            ))}
                        </ol>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SuperPayStatusPanel;
